/**
 * COMMAND PALETTE (Cmd+K)
 *
 * Spotlight-style command palette with fuzzy search.
 * Connected to real stores for projects, agent runs, and navigation.
 */

import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { useLocation } from "wouter";
import {
  Search,
  Settings,
  Sparkles,
  FolderKanban,
  Bot,
  Globe,
  Plus,
  Wrench,
  Eye,
  CreditCard,
} from "lucide-react";
import { useWorldStore } from "@/stores/worldStore";
import { useReviewStore } from "@/stores/reviewStore";

// ─── Types ──────────────────

interface PaletteItem {
  id: string;
  icon: React.ReactNode;
  label: string;
  description?: string;
  shortcut?: string;
  category: string;
  action?: () => void;
}

// ─── Fuzzy Search ──────────────────

function fuzzyScore(text: string, q: string): number {
  const lower = text.toLowerCase();
  const ql = q.toLowerCase();
  if (lower.includes(ql)) return lower.startsWith(ql) ? 100 : 80;
  let qi = 0;
  let score = 0;
  let lastIdx = -1;
  for (let i = 0; i < lower.length && qi < ql.length; i++) {
    if (lower[i] === ql[qi]) {
      score += 10;
      if (lastIdx === i - 1) score += 5;
      if (i === 0 || " -_".includes(text[i - 1])) score += 8;
      lastIdx = i;
      qi++;
    }
  }
  return qi === ql.length ? score : 0;
}

// ─── Shortcut Badge ──────────────────

function ShortcutBadge({ shortcut }: { shortcut: string }) {
  return (
    <span className="flex items-center gap-0.5 shrink-0 ml-2">
      <span
        className="inline-flex items-center h-[18px] px-1.5 rounded-[4px] text-[11px] font-mono"
        style={{
          background: "var(--colony-surface-hover, #222226)",
          border: "1px solid var(--colony-border-subtle)",
          color: "var(--colony-text-tertiary)",
        }}
      >
        {shortcut}
      </span>
    </span>
  );
}

// ─── Command Palette ──────────────────

interface CommandPaletteProps {
  onClose: () => void;
  onQuickFix?: () => void;
  onSettingsOpen?: () => void;
}

export function CommandPalette({ onClose, onQuickFix, onSettingsOpen }: CommandPaletteProps) {
  const [query, setQuery] = useState("");
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const selectedRef = useRef<HTMLButtonElement>(null);
  const [, setLocation] = useLocation();

  const projects = useWorldStore((s) => s.projects);
  const runs = useReviewStore((s) => s.runs);

  // Icon color
  const ic = "text-[color:var(--colony-text-tertiary)]";

  // Build items from real data
  const allItems = useMemo<PaletteItem[]>(() => {
    const items: PaletteItem[] = [];

    // Quick Actions
    items.push(
      { id: "quick-fix", icon: <Wrench size={15} strokeWidth={1.5} className={ic} />, label: "Quick Fix", description: "Create a quick fix task", shortcut: "⌘⇧F", category: "Quick Actions", action: () => { onClose(); onQuickFix?.(); } },
      { id: "nav-agents", icon: <Bot size={15} strokeWidth={1.5} className={ic} />, label: "My Agents", description: "Agent runs dashboard", shortcut: "⌘1", category: "Navigation", action: () => { onClose(); setLocation("/"); } },
      { id: "nav-world", icon: <Globe size={15} strokeWidth={1.5} className={ic} />, label: "World", description: "Isometric world view", shortcut: "⌘2", category: "Navigation", action: () => { onClose(); setLocation("/world"); } },
      { id: "nav-projects", icon: <FolderKanban size={15} strokeWidth={1.5} className={ic} />, label: "Projects", description: "All projects", shortcut: "⌘3", category: "Navigation", action: () => { onClose(); setLocation("/projects"); } },
      { id: "nav-billing", icon: <CreditCard size={15} strokeWidth={1.5} className={ic} />, label: "Billing", description: "Plan, credits & API keys", category: "Navigation", action: () => { onClose(); setLocation("/billing"); } },
      { id: "nav-builder", icon: <Sparkles size={15} strokeWidth={1.5} className={ic} />, label: "Agent Builder", description: "Workflow editor", category: "Navigation", action: () => { onClose(); setLocation("/agent-builder"); } },
      { id: "nav-settings", icon: <Settings size={15} strokeWidth={1.5} className={ic} />, label: "Settings", description: "Workspace preferences", shortcut: "⌘,", category: "Navigation", action: () => { onClose(); onSettingsOpen?.(); } },
    );

    // Real projects
    for (const p of projects) {
      items.push({
        id: `proj-${p.id}`,
        icon: <FolderKanban size={15} strokeWidth={1.5} style={{ color: "var(--colony-green)" }} />,
        label: p.name,
        description: p.githubRepo || undefined,
        category: "Projects",
        action: () => { onClose(); setLocation(`/projects/${p.id}`); },
      });
    }

    // Real agent runs (most recent 10)
    const recentRuns = [...runs].slice(0, 10);
    for (const run of recentRuns) {
      items.push({
        id: `run-${run.id}`,
        icon: <Bot size={15} strokeWidth={1.5} style={{ color: run.status === "running" ? "var(--colony-blue)" : run.status === "blocked" || run.status === "failed" ? "var(--colony-red)" : "var(--colony-green)" }} />,
        label: run.title || `Run #${run.id}`,
        description: `${run.status} · ${run.projectName || ""}`.trim(),
        category: "Agents",
        action: () => { onClose(); setLocation("/"); },
      });
    }

    return items;
  }, [projects, runs, onClose, onQuickFix, onSettingsOpen, setLocation, ic]);

  // Default items (no search)
  const defaultItems = useMemo(() =>
    allItems.filter((i) => i.category === "Quick Actions" || i.category === "Navigation"),
    [allItems]
  );

  // Focus input on mount
  useEffect(() => { inputRef.current?.focus(); }, []);

  // Close on escape + ⌘K toggle
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      if ((e.metaKey || e.ctrlKey) && e.key === "k") { e.preventDefault(); onClose(); }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  // Filter items
  const displayItems = useMemo(() => {
    if (!query.trim()) return defaultItems;
    return allItems
      .map((item) => ({
        ...item,
        score: Math.max(
          fuzzyScore(item.label, query.trim()),
          fuzzyScore(item.description ?? "", query.trim()) * 0.7
        ),
      }))
      .filter((item) => item.score > 0)
      .sort((a, b) => b.score - a.score)
      .slice(0, 12);
  }, [query, allItems, defaultItems]);

  // Group by category
  const grouped = useMemo(() => {
    const map = new Map<string, PaletteItem[]>();
    for (const item of displayItems) {
      if (!map.has(item.category)) map.set(item.category, []);
      map.get(item.category)!.push(item);
    }
    return map;
  }, [displayItems]);

  // Keyboard navigation
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setSelectedIndex((prev) => (prev + 1) % displayItems.length);
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setSelectedIndex((prev) => (prev - 1 + displayItems.length) % displayItems.length);
      } else if (e.key === "Enter") {
        e.preventDefault();
        displayItems[selectedIndex]?.action?.();
      }
    },
    [displayItems, selectedIndex]
  );

  // Reset selection on query change
  useEffect(() => { setSelectedIndex(0); }, [query]);

  // Scroll selected into view
  useEffect(() => { selectedRef.current?.scrollIntoView({ block: "nearest" }); }, [selectedIndex]);

  let flatIndex = -1;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-start justify-center pt-[18vh]"
      onClick={onClose}
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0"
        style={{
          background: "rgba(0, 0, 0, 0.55)",
          backdropFilter: "blur(4px)",
          WebkitBackdropFilter: "blur(4px)",
          animation: "colony-fadeIn 100ms ease-out",
        }}
      />

      {/* Palette */}
      <div
        className="relative w-full max-w-[560px] rounded-[12px] overflow-hidden flex flex-col"
        style={{
          maxHeight: 420,
          background: "var(--glass-bg, rgba(26, 26, 30, 0.92))",
          backdropFilter: "var(--glass-blur, blur(24px) saturate(1.3))",
          WebkitBackdropFilter: "var(--glass-blur, blur(24px) saturate(1.3))",
          border: "1px solid var(--glass-border, rgba(255, 255, 255, 0.06))",
          boxShadow: "var(--glass-shadow, 0 24px 80px rgba(0,0,0,0.55), 0 8px 32px rgba(0,0,0,0.4))",
          animation: "colony-paletteIn 120ms var(--ease-out-expo)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Search input */}
        <div
          className="flex items-center gap-3 h-10 px-4 shrink-0"
          style={{ borderBottom: "1px solid var(--colony-border-subtle)" }}
        >
          <Search size={16} strokeWidth={1.5} style={{ color: "var(--colony-text-tertiary)" }} />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Search commands, projects, agents..."
            aria-label="Search commands"
            className="flex-1 bg-transparent text-[15px] outline-none"
            style={{ color: "var(--colony-text-primary)" }}
          />
          {query && (
            <button
              onClick={() => setQuery("")}
              className="text-[10px] px-1.5 py-0.5 rounded-[3px] transition-colors cursor-pointer"
              style={{ color: "var(--colony-text-tertiary)", background: "var(--colony-surface-hover)" }}
            >
              Clear
            </button>
          )}
        </div>

        {/* Results */}
        <div className="flex-1 overflow-y-auto py-1.5 colony-scrollbar">
          {displayItems.length === 0 ? (
            <div className="px-4 py-10 text-center">
              <Search size={24} strokeWidth={1} className="mx-auto mb-2 opacity-40" style={{ color: "var(--colony-text-tertiary)" }} />
              <span className="text-[13px]" style={{ color: "var(--colony-text-tertiary)" }}>
                No results for &ldquo;{query}&rdquo;
              </span>
            </div>
          ) : (
            Array.from(grouped.entries()).map(([category, items]) => (
              <div key={category} className="mb-0.5">
                <div className="px-4 pt-2 pb-1">
                  <span className="text-[11px] font-medium uppercase tracking-[0.06em]" style={{ color: "var(--colony-text-tertiary)" }}>
                    {category}
                  </span>
                </div>
                {items.map((item) => {
                  flatIndex++;
                  const currentIndex = flatIndex;
                  const isSelected = currentIndex === selectedIndex;
                  return (
                    <button
                      key={item.id}
                      ref={isSelected ? selectedRef : undefined}
                      onClick={() => item.action?.()}
                      onMouseEnter={() => setSelectedIndex(currentIndex)}
                      className="relative w-full flex items-center gap-3 px-4 h-9 transition-colors duration-75 cursor-pointer"
                      style={{
                        background: isSelected ? "var(--colony-brand-muted)" : "transparent",
                      }}
                    >
                      {isSelected && (
                        <div className="absolute left-0 top-1 bottom-1 w-[2px] rounded-full" style={{ background: "var(--colony-brand)" }} />
                      )}
                      <span className="shrink-0" style={{ color: isSelected ? "var(--colony-brand)" : undefined }}>
                        {item.icon}
                      </span>
                      <span
                        className="text-[13px] font-medium whitespace-nowrap shrink-0"
                        style={{ color: isSelected ? "var(--colony-text-primary)" : "var(--colony-text-secondary)" }}
                      >
                        {item.label}
                      </span>
                      {item.description && (
                        <span className="text-[12px] truncate flex-1 text-left" style={{ color: "var(--colony-text-tertiary)" }}>
                          {item.description}
                        </span>
                      )}
                      {!item.description && <span className="flex-1" />}
                      {item.shortcut && <ShortcutBadge shortcut={item.shortcut} />}
                    </button>
                  );
                })}
              </div>
            ))
          )}
        </div>

        {/* Footer */}
        <div
          className="flex items-center gap-5 px-4 h-9 shrink-0"
          style={{
            borderTop: "1px solid var(--colony-border-subtle)",
            background: "rgba(0, 0, 0, 0.15)",
          }}
        >
          {[
            { keys: ["↑", "↓"], label: "Navigate" },
            { keys: ["↵"], label: "Select" },
            { keys: ["esc"], label: "Close" },
          ].map(({ keys, label }) => (
            <div key={label} className="flex items-center gap-1.5 text-[10px]" style={{ color: "var(--colony-text-tertiary)" }}>
              {keys.map((k) => (
                <kbd
                  key={k}
                  className="inline-flex items-center justify-center h-[16px] px-1 rounded-[3px] text-[9px] font-mono"
                  style={{ background: "var(--colony-surface-hover)", border: "1px solid var(--colony-border-subtle)" }}
                >
                  {k}
                </kbd>
              ))}
              <span className="ml-0.5">{label}</span>
            </div>
          ))}
          <div className="flex-1" />
          {query.trim() && displayItems.length > 0 && (
            <span className="text-[10px] tabular-nums" style={{ color: "var(--colony-text-tertiary)" }}>
              {displayItems.length} result{displayItems.length !== 1 ? "s" : ""}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
